/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 开发工具
 * 提供开发环境下的调试和诊断功能
 */

import { devLog, devWarn, devError } from './typescript-helpers'
import { errorHandler } from './error-handler'

// 开发工具类
export class DevTools {
  private static instance: DevTools
  private isEnabled = import.meta.env.DEV
  private debugMode = false

  private constructor() {
    if (this.isEnabled) {
      this.setupDevTools()
    }
  }

  public static getInstance(): DevTools {
    if (!DevTools.instance) {
      DevTools.instance = new DevTools()
    }
    return DevTools.instance
  }

  /**
   * 设置开发工具
   */
  private setupDevTools(): void {
    // 添加全局调试方法
    ;(window as any).__DEV_TOOLS__ = {
      // 错误处理相关
      getErrors: () => errorHandler.getErrorQueue(),
      clearErrors: () => errorHandler.clearErrorQueue(),
      
      // 组件调试
      inspectComponent: this.inspectComponent.bind(this),
      
      // 性能分析
      startPerformanceProfile: this.startPerformanceProfile.bind(this),
      endPerformanceProfile: this.endPerformanceProfile.bind(this),
      
      // 状态检查
      checkStores: this.checkStores.bind(this),
      
      // 路由调试
      inspectRouter: this.inspectRouter.bind(this),
      
      // 网络请求调试
      inspectNetworkRequests: this.inspectNetworkRequests.bind(this),
      
      // 控制台清理
      cleanConsole: this.cleanConsole.bind(this),
      
      // 调试模式切换
      toggleDebug: this.toggleDebug.bind(this),
      
      // 系统信息
      getSystemInfo: this.getSystemInfo.bind(this)
    }

    // 添加快捷键
    this.setupKeyboardShortcuts()

    devLog('Dev Tools initialized. Access via window.__DEV_TOOLS__')
  }

  /**
   * 设置键盘快捷键
   */
  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (event) => {
      // Ctrl + Shift + D: 切换调试模式
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault()
        this.toggleDebug()
      }

      // Ctrl + Shift + C: 清理控制台
      if (event.ctrlKey && event.shiftKey && event.key === 'C') {
        event.preventDefault()
        this.cleanConsole()
      }

      // Ctrl + Shift + E: 显示错误信息
      if (event.ctrlKey && event.shiftKey && event.key === 'E') {
        event.preventDefault()
        this.showErrors()
      }

      // Ctrl + Shift + I: 显示系统信息
      if (event.ctrlKey && event.shiftKey && event.key === 'I') {
        event.preventDefault()
        this.showSystemInfo()
      }
    })
  }

  /**
   * 检查组件
   */
  private inspectComponent(componentName?: string): void {
    const app = (window as any).__VUE_APP__
    if (!app) {
      devWarn('Vue app instance not found')
      return
    }

    devLog('Inspecting Vue components:', {
      app,
      components: app.config.globalProperties,
      componentName
    })
  }

  /**
   * 开始性能分析
   */
  private startPerformanceProfile(name: string = 'default'): void {
    performance.mark(`profile-${name}-start`)
    devLog(`Performance profile started: ${name}`)
  }

  /**
   * 结束性能分析
   */
  private endPerformanceProfile(name: string = 'default'): void {
    const startMark = `profile-${name}-start`
    const endMark = `profile-${name}-end`
    
    performance.mark(endMark)
    performance.measure(`profile-${name}`, startMark, endMark)
    
    const measure = performance.getEntriesByName(`profile-${name}`)[0]
    devLog(`Performance profile completed: ${name}`, {
      duration: `${measure.duration.toFixed(2)}ms`,
      startTime: measure.startTime,
      endTime: measure.startTime + measure.duration
    })
  }

  /**
   * 检查 Pinia stores
   */
  private checkStores(): void {
    const pinia = (window as any).__PINIA__
    if (!pinia) {
      devWarn('Pinia instance not found')
      return
    }

    devLog('Pinia stores:', pinia)
  }

  /**
   * 检查路由器
   */
  private inspectRouter(): void {
    const router = (window as any).__VUE_ROUTER__
    if (!router) {
      devWarn('Vue Router instance not found')
      return
    }

    devLog('Vue Router:', {
      currentRoute: router.currentRoute.value,
      routes: router.getRoutes(),
      history: router.options.history
    })
  }

  /**
   * 检查网络请求
   */
  private inspectNetworkRequests(): void {
    // 拦截 fetch 请求
    const originalFetch = window.fetch
    window.fetch = async (...args) => {
      const startTime = performance.now()
      devLog('Network request started:', args[0])
      
      try {
        const response = await originalFetch(...args)
        const endTime = performance.now()
        
        devLog('Network request completed:', {
          url: args[0],
          status: response.status,
          duration: `${(endTime - startTime).toFixed(2)}ms`
        })
        
        return response
      } catch (error) {
        const endTime = performance.now()
        devError('Network request failed:', {
          url: args[0],
          error,
          duration: `${(endTime - startTime).toFixed(2)}ms`
        })
        throw error
      }
    }
  }

  /**
   * 清理控制台
   */
  private cleanConsole(): void {
    console.clear()
    devLog('Console cleared')
  }

  /**
   * 切换调试模式
   */
  private toggleDebug(): void {
    this.debugMode = !this.debugMode
    devLog(`Debug mode ${this.debugMode ? 'enabled' : 'disabled'}`)
    
    if (this.debugMode) {
      // 启用详细日志
      this.enableVerboseLogging()
    } else {
      // 禁用详细日志
      this.disableVerboseLogging()
    }
  }

  /**
   * 启用详细日志
   */
  private enableVerboseLogging(): void {
    // 拦截所有 console 方法
    const originalMethods = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug
    }

    Object.keys(originalMethods).forEach(method => {
      ;(console as any)[method] = (...args: any[]) => {
        const timestamp = new Date().toISOString()
        const stack = new Error().stack?.split('\n').slice(2, 4).join('\n')
        
        ;(originalMethods as any)[method](
          `%c[${timestamp}] %c[${method.toUpperCase()}]`,
          'color: #666; font-size: 11px;',
          `color: ${this.getLogColor(method)}; font-weight: bold;`,
          ...args
        )
        
        if (this.debugMode) {
          console.groupCollapsed('Stack trace')
          console.log(stack)
          console.groupEnd()
        }
      }
    })
  }

  /**
   * 禁用详细日志
   */
  private disableVerboseLogging(): void {
    // 恢复原始 console 方法
    // 这里可以实现恢复逻辑
  }

  /**
   * 获取日志颜色
   */
  private getLogColor(method: string): string {
    const colors = {
      log: '#2196F3',
      warn: '#FF9800',
      error: '#F44336',
      info: '#4CAF50',
      debug: '#9C27B0'
    }
    return (colors as any)[method] || '#666'
  }

  /**
   * 显示错误信息
   */
  private showErrors(): void {
    const errors = errorHandler.getErrorQueue()
    if (errors.length === 0) {
      devLog('No errors found')
      return
    }

    console.group('🚨 Error Queue')
    errors.forEach((error, index) => {
      console.group(`Error ${index + 1}`)
      console.error('Message:', error.message)
      console.log('Source:', error.source)
      console.log('Timestamp:', new Date(error.timestamp).toISOString())
      console.log('URL:', error.url)
      if (error.stack) {
        console.log('Stack:', error.stack)
      }
      console.groupEnd()
    })
    console.groupEnd()
  }

  /**
   * 显示系统信息
   */
  private showSystemInfo(): void {
    const info = this.getSystemInfo()
    console.group('💻 System Information')
    Object.entries(info).forEach(([key, value]) => {
      console.log(`${key}:`, value)
    })
    console.groupEnd()
  }

  /**
   * 获取系统信息
   */
  private getSystemInfo(): Record<string, any> {
    return {
      'User Agent': navigator.userAgent,
      'Platform': navigator.platform,
      'Language': navigator.language,
      'Languages': navigator.languages,
      'Online': navigator.onLine,
      'Cookie Enabled': navigator.cookieEnabled,
      'Screen Resolution': `${screen.width}x${screen.height}`,
      'Viewport Size': `${window.innerWidth}x${window.innerHeight}`,
      'Device Pixel Ratio': window.devicePixelRatio,
      'Memory': (navigator as any).deviceMemory || 'Unknown',
      'Hardware Concurrency': navigator.hardwareConcurrency,
      'Connection': (navigator as any).connection?.effectiveType || 'Unknown',
      'Vue Version': (window as any).__VUE_VERSION__ || 'Unknown',
      'Environment': import.meta.env.MODE,
      'Base URL': import.meta.env.BASE_URL,
      'API Base URL': import.meta.env.VITE_API_BASE_URL || 'Not set'
    }
  }
}

// 导出单例实例
export const devTools = DevTools.getInstance()

// 默认导出
export default devTools